import {
  PetPolicyTemplateSettings,
  PetPolicyCustomRule,
  PetRestriction,
  PetRestrictionCategory,
  PetRestrictionType
} from "../types/pet-policy";
import { has } from "./has";

const asCustomRule = (restriction: PetRestriction): PetPolicyCustomRule => ({
  title: (restriction.title || "").trim(),
  description:
    typeof restriction.value === "string" && restriction.value.trim()
      ? restriction.value.trim()
      : null
});

/** Converts PX form state -> API shape */
export const encodePetPolicy = (
  template: Omit<PetPolicyTemplateSettings, "general" | "cats" | "dogs">
): PetPolicyTemplateSettings => {
  const policy: PetPolicyTemplateSettings = {
    enabled: Boolean(template.enabled)
  };

  for (const restriction of template.restrictions || []) {
    if (restriction.category === PetRestrictionCategory.General) {
      if (!policy.general) policy.general = {};
      switch (restriction.type) {
        case PetRestrictionType.Count:
          policy.general.max_pets_count = restriction.value as number;
          break;
        case PetRestrictionType.ServiceAnimal:
          policy.general.service_animals_allowed = restriction.value as boolean;
          break;
        case PetRestrictionType.Esa:
          policy.general.esa_allowed = restriction.value as boolean;
          break;
        case PetRestrictionType.CustomField:
          if (!policy.general.custom_rules) policy.general.custom_rules = [];
          policy.general.custom_rules.push(asCustomRule(restriction));
          break;
      }
      continue;
    }

    const species =
      restriction.category === PetRestrictionCategory.Dog ? "dogs" : "cats";
    if (!policy[species]) policy[species] = {};
    const group = policy[species] || {};

    switch (restriction.type) {
      case PetRestrictionType.Allowed:
        group.allowed = restriction.value as boolean;
        break;
      case PetRestrictionType.Weight:
        group.max_weight_lbs = restriction.value as number;
        break;
      case PetRestrictionType.Breed:
        if (!group.restricted_breeds) group.restricted_breeds = [];
        if (typeof restriction.value === "string" && restriction.value.trim())
          group.restricted_breeds.push(restriction.value.trim());
        break;
      case PetRestrictionType.CustomField:
        if (!group.custom_rules) group.custom_rules = [];
        group.custom_rules.push(asCustomRule(restriction));
        break;
    }
  }

  return { ...policy, restrictions: template.restrictions };
};

/** Converts API shape -> PX form state */
export const petPolicyToRestrictions = (
  policy: PetPolicyTemplateSettings
): PetRestriction[] => {
  const rows: PetRestriction[] = [];
  const addRow = (
    category: PetRestrictionCategory,
    type: PetRestrictionType,
    value: PetRestriction["value"],
    title?: string
  ) => rows.push({ category, type, value, ...(title ? { title } : {}) });

  const { general } = policy;
  if (general) {
    if (has(general?.max_pets_count))
      addRow(
        PetRestrictionCategory.General,
        PetRestrictionType.Count,
        general.max_pets_count
      );
    if (has(general.service_animals_allowed))
      addRow(
        PetRestrictionCategory.General,
        PetRestrictionType.ServiceAnimal,
        general.service_animals_allowed
      );
    if (has(general.esa_allowed))
      addRow(
        PetRestrictionCategory.General,
        PetRestrictionType.Esa,
        general.esa_allowed
      );
    for (const rule of general.custom_rules || [])
      addRow(
        PetRestrictionCategory.General,
        PetRestrictionType.CustomField,
        rule.description || "",
        rule.title
      );
  }

  for (const category of [
    PetRestrictionCategory.Dog,
    PetRestrictionCategory.Cat
  ] as const) {
    const species = category === PetRestrictionCategory.Dog ? "dogs" : "cats";
    const group = policy[species];
    if (!group) continue;

    if (has(group.allowed))
      addRow(category, PetRestrictionType.Allowed, group.allowed);
    if (has(group.max_weight_lbs))
      addRow(category, PetRestrictionType.Weight, group.max_weight_lbs);
    for (const breed of group.restricted_breeds || [])
      addRow(category, PetRestrictionType.Breed, breed);
    for (const rule of group.custom_rules || [])
      addRow(
        category,
        PetRestrictionType.CustomField,
        rule.description || "",
        rule.title
      );
  }

  return rows;
};

const PET_RESTRICTION_LABELS = {
  [PetRestrictionType.Count]: "Pet count",
  [PetRestrictionType.ServiceAnimal]: "Service Animal",
  [PetRestrictionType.Esa]: "ESA",
  [PetRestrictionType.Allowed]: "Allowed Yes/No",
  [PetRestrictionType.Weight]: "Weight",
  [PetRestrictionType.Breed]: "Breed",
  [PetRestrictionType.CustomField]: "Custom field"
};

export const detectDuplicateRestrictionType = (
  restrictions: PetRestriction[]
) => {
  const seenRestrictions = new Set<string>();

  for (const restriction of restrictions) {
    if (restriction.type === PetRestrictionType.CustomField) continue;

    const key = `${restriction.category}-${restriction.type}`;
    if (seenRestrictions.has(key))
      return PET_RESTRICTION_LABELS[restriction.type];

    seenRestrictions.add(key);
  }

  return null;
};
